import { isNativeEmbed } from './isNativeEmbed';

/**
 * Admin "Log in as this user" handoff.
 *
 * An OWNER of the Music24 admin opens `{webApp}/#loginAs=<code>` in a new tab. The code is single
 * use and lives 60 seconds; `LoginAsGate` exchanges it for a session token of that user.
 *
 * The rules, and each one is load-bearing:
 *
 * - **The code travels in the fragment, never in the query.** A fragment is not sent to a server,
 *   so the code is in no access log.
 * - **The fragment always goes**, also when the value is malformed, so a copied address never
 *   carries a code.
 * - **The code stays in memory only.** It is never stored and never logged.
 * - **A native app's web view ignores it.** The apps have their own sign-in; a `#loginAs` there is
 *   stripped and nothing more.
 *
 * `captureLoginAsCode` runs at module load (`AppInitialEntry`), next to the native token handoff.
 */
export const loginAsParamName = 'loginAs';

/** The code is base64url, about 43 characters. Any other value is refused. */
const loginAsCodePattern = /^[A-Za-z0-9_-]{20,200}$/;

export function isLoginAsCode(value: string): boolean {
  return loginAsCodePattern.test(value);
}

/** The parameters of a URL fragment. Anything that is not a fragment reads as empty. */
function fragmentParams(hash: string): URLSearchParams {
  if (!hash.startsWith('#')) {
    return new URLSearchParams();
  }
  return new URLSearchParams(hash.slice(1));
}

/** Whether the fragment names a login-as code at all, whatever its value is. */
export function hasLoginAsFragment(hash: string): boolean {
  return fragmentParams(hash).has(loginAsParamName);
}

/** The code the fragment carries, or null when there is none or it is malformed. */
export function extractLoginAsCode(hash: string): string | null {
  const code = fragmentParams(hash).get(loginAsParamName);
  if (code === null || !isLoginAsCode(code)) {
    return null;
  }
  return code;
}

/** Same url with `loginAs` removed from the fragment; every other parameter stays put. */
export function stripLoginAsFromUrl(href: string, base: string): string {
  const url = new URL(href, base);
  const params = fragmentParams(url.hash);
  params.delete(loginAsParamName);
  const rest = params.toString();
  return `${url.pathname}${url.search}${rest === '' ? '' : `#${rest}`}`;
}

let capturedCode: string | null = null;

export function captureLoginAsCode(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  if (!hasLoginAsFragment(window.location.hash)) {
    return null;
  }
  const code = extractLoginAsCode(window.location.hash);
  const nativeEmbed = isNativeEmbed(window.navigator.userAgent);
  // The fragment goes whatever happens next: a code must not stay in the address bar.
  window.history.replaceState(null, '', stripLoginAsFromUrl(window.location.href, window.location.origin));
  if (code === null || nativeEmbed) {
    return null;
  }
  capturedCode = code;
  return code;
}

/**
 * The captured code. It stays until `clearLoginAsCode`, so a second render (React strict mode)
 * reads the same value.
 */
export function peekLoginAsCode(): string | null {
  return capturedCode;
}

/** Forgets the code. Call it when the exchange ended. */
export function clearLoginAsCode(): void {
  capturedCode = null;
}
